/* Personalizer v3 core renderers: segmented stepper, sticky-bar totals,
   dialogs (size chart + photo guide), one-time static text fills
   (photo hint, notes label/placeholder, guide list, size chart values).
   Step-content renderers (counts, type cards, swatches, mockups) live in
   personalizer-render-steps.js and attach to the same window.PersonalizerRender
   namespace. All data comes from the JSON embed via ctx.config. */
(function () {
  'use strict';

  var STEP_LABELS = { type: 'Set', size: 'Size', besties: 'Besties', colors: 'Colours', photos: 'Photos', card: 'Card', review: 'Extras' };

  /* Step-1 tab shows the product's own unit noun ("Pets" on pet products,
     "Besties" default). config.stepLabels can override any tab outright. */
  function stepLabel(ctx, name) {
    var overrides = ctx.config.stepLabels || {};
    if (overrides[name]) return overrides[name];
    if (name === 'besties') return (ctx.config.unitLabel || 'Bestie') + 's';
    return STEP_LABELS[name] || name;
  }

  function money(ctx, cents) {
    return window.PersonalizerCart.formatMoney(cents, ctx.config.moneyFormat);
  }

  /* Segmented stepper from the SAME steps array that drives navigation (see
     personalizer.js) so the segments can never disagree with the flow.
     done (before the active step + valid) = mint + "✓ " prefix;
     active = forest + white; upcoming = transparent muted with "N." prefix.
     reachableMax = index of the furthest clickable segment (first invalid
     step caps forward jumps; revisiting is always allowed). */
  function renderTabs(ctx, steps, activeIdx, reachableMax) {
    var bar = ctx.root.querySelector('[data-personalizer-tabs]');
    if (!bar) return;
    bar.innerHTML = '';
    steps.forEach(function (name, i) {
      var tab = document.createElement('button');
      tab.type = 'button';
      tab.id = 'personalizer-tab-' + name;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', 'personalizer-panel-' + name);
      tab.setAttribute('aria-selected', i === activeIdx ? 'true' : 'false');
      var isDone = i < activeIdx && ctx.stepValid(name);
      tab.className = 'personalizer__stp' +
        (i === activeIdx ? ' personalizer__stp--active' : (isDone ? ' personalizer__stp--done' : ''));
      var n = document.createElement('span');
      n.className = 'personalizer__stp-n';
      n.textContent = isDone ? '✓' : (i + 1) + '.';
      tab.appendChild(n);
      tab.appendChild(document.createTextNode(stepLabel(ctx, name)));
      if (i > reachableMax) tab.disabled = true;
      tab.addEventListener('click', function () {
        if (!tab.disabled) ctx.goToStep(name);
      });
      bar.appendChild(tab);
    });
  }

  /* Sticky-bar money readout: plum total + struck compare (only when any
     picked type carries a compareAt — i.e. a Full Set is in the order). */
  function updateBar(ctx) {
    var totalEl = ctx.root.querySelector('[data-personalizer-running-total]');
    if (!totalEl) return;
    totalEl.textContent = money(ctx, window.PersonalizerCart.totalCents(ctx));
    var compareCents = window.PersonalizerCart.compareTotalCents(ctx);
    var cmp = ctx.root.querySelector('[data-personalizer-running-compare]');
    if (cmp) {
      var show = compareCents > window.PersonalizerCart.totalCents(ctx);
      cmp.hidden = !show;
      cmp.textContent = show ? money(ctx, compareCents) : '';
    }
  }

  /* Generic dialog wiring: native <dialog> (ESC + focus containment) with an
     open-attribute fallback incl. manual ESC and a basic Tab focus trap. */
  function wireDialog(ctx, openSel, dlgSel, closeSel) {
    var openBtn = ctx.root.querySelector(openSel);
    var dlg = ctx.root.querySelector(dlgSel);
    if (!openBtn || !dlg) return;
    var native = typeof dlg.showModal === 'function';

    function open() {
      // Relocate to <body> first: Boost's product page has transformed
      // ancestors (sticky media), which turn the dialog's position:fixed into
      // ancestor-relative — mispositioning the modal. As a direct child of
      // <body> there is no transformed ancestor, so fixed = viewport = centred.
      if (dlg.parentNode !== document.body) document.body.appendChild(dlg);
      try {
        if (native) dlg.showModal();
        else dlg.setAttribute('open', '');
      } catch (err) {
        dlg.setAttribute('open', '');
      }
      var closeBtn = dlg.querySelector(closeSel);
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      try {
        if (native && dlg.open) dlg.close();
        else dlg.removeAttribute('open');
      } catch (err) {
        dlg.removeAttribute('open');
      }
      openBtn.focus(); // return focus to the trigger
    }

    openBtn.addEventListener('click', open);
    dlg.querySelector(closeSel).addEventListener('click', close);
    // Backdrop click: the dialog element itself is the click target only outside the inner content.
    dlg.addEventListener('click', function (e) { if (e.target === dlg) close(); });
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); }); // native ESC
    dlg.addEventListener('keydown', function (e) {
      if (!native && e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        var f = dlg.querySelectorAll('button');
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* One-time fills of config-driven static copy + both dialogs' content.
     Called once at init; nothing here re-renders on state changes. */
  function initStaticContent(ctx) {
    var cfg = ctx.config;

    // Step-1 question ("How many besties?" default lives in the snippet).
    var countQ = ctx.root.querySelector('[data-personalizer-count-q]');
    if (countQ && cfg.countQuestion) countQ.textContent = cfg.countQuestion;

    // Optional one-liner under the question explaining what each count means
    // (e.g. "every pet = its own doll + embroidered name, on one tote").
    // The node exists once per panel (type AND count) — fill every copy:
    // a single-type product never opens the type panel, so filling only the
    // first match leaves the count page's hint permanently hidden.
    if (cfg.countHint) {
      ctx.root.querySelectorAll('[data-personalizer-count-hint]').forEach(function (el) {
        el.textContent = cfg.countHint;
        el.hidden = false;
      });
    }

    var hint = ctx.root.querySelector('[data-photo-hint]');
    if (hint) hint.textContent = cfg.photoHint || '';

    var notesLabel = ctx.root.querySelector('[data-notes-label]');
    if (notesLabel) notesLabel.textContent = cfg.notesLabel || 'Notes';
    // What these two lines say depends entirely on what is being made, so the
    // product's own config owns them — a bag sentence on a nursery mobile
    // reads like someone else's page.
    var notesHint = ctx.root.querySelector('[data-notes-hint]');
    if (notesHint && cfg.notesHint) notesHint.textContent = cfg.notesHint;
    var extrasHint = ctx.root.querySelector('[data-extras-hint]');
    if (extrasHint && cfg.extrasHint) extrasHint.textContent = cfg.extrasHint;
    var notes = ctx.root.querySelector('[data-personalizer-notes]');
    if (notes) notes.placeholder = cfg.notesPlaceholder || '';

    // Photo guide: one <li> per non-empty uploadGuide line.
    var list = ctx.root.querySelector('[data-guide-list]');
    if (list) {
      list.innerHTML = '';
      String(cfg.uploadGuide || '').split(/\r?\n/).forEach(function (line) {
        var text = line.trim();
        if (!text) return;
        var li = document.createElement('li');
        li.textContent = text;
        list.appendChild(li);
      });
    }

    // Size chart: table cells verbatim; the drawing's two callouts use the
    // measurement's first "·"-separated segment (metric part).
    var sc = cfg.sizeChart || {};
    ctx.root.querySelectorAll('[data-size-cell]').forEach(function (cell) {
      cell.textContent = sc[cell.getAttribute('data-size-cell')] || '—';
    });
    var svgH = ctx.root.querySelector('[data-size-svg-height]');
    var svgW = ctx.root.querySelector('[data-size-svg-width]');
    if (svgH && sc.height) svgH.textContent = String(sc.height).split('·')[0].trim();
    if (svgW && sc.widthTop) svgW.textContent = String(sc.widthTop).split('·')[0].trim();

    // Quiet pages: dialogs are relocated to <body> on open (see wireDialog), so
    // the .personalizer--quiet ancestor selector stops matching the moment one
    // opens. Stamping the class on each dialog keeps the decorations hidden.
    if (ctx.root.classList.contains('personalizer--quiet')) {
      ctx.root.querySelectorAll('dialog.personalizer__dialog').forEach(function (d) {
        d.classList.add('personalizer--quiet');
      });
    }

    wireDialog(ctx, '[data-guide-open]', '[data-guide-modal]', '[data-guide-close]');
    wireDialog(ctx, '[data-size-open]', '[data-size-modal]', '[data-size-close]');

    // Size guide (config.sizeGuide): its open button lives in the JS-rendered
    // axis row, which renderAxis rebuilds — so opening is DELEGATED from the
    // root while closing is wired once on the dialog itself.
    var sg = ctx.root.querySelector('[data-sizeguide-modal]');
    if (cfg.sizeGuide && sg) {
      var sgNative = typeof sg.showModal === 'function';
      var sgTrigger = null;
      var sgOpen = function (btn) {
        sgTrigger = btn;
        if (sg.parentNode !== document.body) document.body.appendChild(sg);
        try { if (sgNative) sg.showModal(); else sg.setAttribute('open', ''); }
        catch (err) { sg.setAttribute('open', ''); }
        var c = sg.querySelector('[data-sizeguide-close]');
        if (c) c.focus();
      };
      var sgClose = function () {
        try { if (sgNative && sg.open) sg.close(); else sg.removeAttribute('open'); }
        catch (err) { sg.removeAttribute('open'); }
        if (sgTrigger) sgTrigger.focus();
      };
      ctx.root.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-sizeguide-open]') : null;
        if (btn) sgOpen(btn);
      });
      sg.querySelector('[data-sizeguide-close]').addEventListener('click', sgClose);
      sg.addEventListener('click', function (e) { if (e.target === sg) sgClose(); });
      sg.addEventListener('cancel', function (e) { e.preventDefault(); sgClose(); });
    }

    // "Only 1 pet?" nudge dialog: one pet crocheted several times keeps a
    // nursery mobile full and balanced, and gives a bag a matching pair of
    // charms. Count-mode products fill it once from config.onePetGuide; bag
    // products rewrite it per count in updateSamePetGuide (which also owns
    // the button's visibility), so they only need the wiring here.
    var onePet = cfg.onePetGuide;
    var onePetBtn = ctx.root.querySelector('[data-onepet-open]');
    if (onePetBtn && onePet) {
      onePetBtn.hidden = false;
      if (onePet.button) onePetBtn.textContent = onePet.button;
      var opTitle = ctx.root.querySelector('[data-onepet-title]');
      if (opTitle && onePet.title) opTitle.textContent = onePet.title;
      var opText = ctx.root.querySelector('[data-onepet-text]');
      if (opText && onePet.text) opText.textContent = onePet.text;
    }
    if (onePetBtn && (onePet || cfg.bagMode === 'oneBag')) {
      wireDialog(ctx, '[data-onepet-open]', '[data-onepet-modal]', '[data-onepet-close]');
    }
  }

  window.PersonalizerRender = {
    renderTabs: renderTabs,
    updateBar: updateBar,
    initStaticContent: initStaticContent
  };
})();
